<template>
  <div class="analysis-container">
    <!-- 顶部 -->
    <div class="top-bar">
      <button class="ghost-btn" @click="goBack">
        <span class="icon">←</span> 返回
      </button>
      <div class="title">{{ toolInfo.title || 'Step1：基因家族鉴定' }}</div>
      <div class="actions">
        <button class="tool-btn" @click="showGuide = true">❓ 使用指南</button>
      </div>
    </div>

    <!-- 主体区域 -->
    <div class="main-layout">
      <!-- 左侧结果展示 -->
      <div class="left-panel">
        <!-- 顶部按钮栏 -->
        <div class="chart-toolbar">
          <button class="tool-btn" :class="{ primary: activeTab === 'guide' }" @click="activeTab = 'guide'">❓ 使用指南</button>
          <button class="tool-btn" :class="{ primary: activeTab === 'logs' }" @click="activeTab = 'logs'" :disabled="!taskInfo">📋 执行日志</button>
          <button 
            class="tool-btn" 
            :class="{ primary: activeTab === 'result', disabled: !canShowResult }" 
            @click="handleResultClick"
          >
            📊 分析结果
            <span v-if="taskInfo && taskInfo.status === 'RUNNING'" class="btn-status">(运行中)</span>
            <span v-else-if="taskInfo && taskInfo.status === 'PENDING'" class="btn-status">(等待中)</span>
          </button>
        </div>

        <!-- 内容区域 -->
        <div class="content-area">
          <!-- 使用指南 -->
          <div v-if="activeTab === 'guide'" class="guide-panel">
            <div v-if="toolInfo.guide" class="guide-content" v-html="toolInfo.guide"></div>
            <div v-else class="guide-content">
              <h3>🧬 基因家族鉴定工具使用指南</h3>
              <h4>工具简介</h4>
              <p>本工具用于根据选定的基因组或者泛基因组进行特定基因家族的鉴定。基于 BLAST 和 HMMER 算法，通过比对已知家族蛋白序列，鉴定出目标基因组中的基因家族成员。</p>
              
              <h4>必填参数</h4>
              <ul>
                <li><strong>已知家族蛋白序列</strong>：上传已知基因家族的蛋白序列文件（FASTA格式），用于作为参考序列进行比对</li>
                <li><strong>基因组文件夹</strong>：从服务器选择目标基因组数据所在的文件夹</li>
                <li><strong>家族简写名称</strong>：输入基因家族的英文简写，如 WRKY、MYB、bHLH 等，将用于基因重命名</li>
              </ul>
              
              <h4>可选参数</h4>
              <ul>
                <li><strong>Pfam文件</strong>：上传Pfam隐马尔可夫模型文件，用于HMMER搜索</li>
                <li><strong>比对过滤比值</strong>：控制BLAST结果过滤的严格程度（0.1-0.8），值越高过滤越严格</li>
                <li><strong>分组文件</strong>：基因家族亚族分组信息，用于后续分类统计</li>
                <li><strong>最低一致性</strong>：BLAST比对的一致性阈值（%）</li>
                <li><strong>最低得分</strong>：BLAST比对的得分阈值</li>
                <li><strong>取并集</strong>：使用BLAST和HMMER结果的并集（默认使用交集）</li>
                <li><strong>仅用HMMER</strong>：仅使用HMMER搜索结果</li>
              </ul>
              
              <h4>输出结果</h4>
              <ul>
                <li>鉴定出的基因家族成员序列（FASTA格式）</li>
                <li>基因结构信息（如果有GFF文件）</li>
                <li>染色体定位信息</li>
                <li>结构域分析结果</li>
                <li>系统发育树构建所需文件</li>
              </ul>
            </div>
          </div>

          <!-- 日志展示 -->
          <div v-else-if="activeTab === 'logs'" class="logs-panel">
            <div class="logs-header">
              <span class="logs-title">实时执行日志</span>
              <span class="polling-status">● 轮询模式 (2秒刷新)</span>
            </div>
            <div ref="logsContainer" class="logs-container">
              <div v-if="logs.length === 0" class="empty-logs">等待任务开始...</div>
              <div v-for="(log, index) in logs" :key="index" :class="['log-item', log.type]">
                <span class="log-time">{{ formatTime(log.time) }}</span>
                <span class="log-type-badge">{{ log.type }}</span>
                <span class="log-message">{{ log.message }}</span>
              </div>
            </div>
          </div>

          <!-- 结果展示 -->
          <div v-else-if="activeTab === 'result'" class="result-panel">
            <!-- 分析成功 -->
            <div v-if="taskInfo" class="result-content">
              <div class="result-header">
                <span class="success-icon">✅</span>
                <span class="success-text">分析成功完成！</span>
              </div>
              
              <!-- 输出目录路径 -->
              <div v-if="taskInfo.outputDir" class="output-dir-info">
                <div class="dir-label">📂 输出目录 (-od):</div>
                <div class="dir-path">{{ taskInfo.outputDir }}</div>
              </div>
              
              <!-- 目录树状结构 -->
              <div class="result-tree-panel">
                <div class="tree-header">
                  <h4>📁 结果目录结构</h4>
                  <button class="primary-btn small" @click="downloadAllResults">
                    <span>📦</span> 下载全部
                  </button>
                </div>
                
                <div class="tree-container">
                  <div v-if="loadingTree" class="loading-tree">加载目录结构中...</div>
                  <div v-else-if="resultTree.length === 0" class="empty-tree">暂无结果文件</div>
                  <tree-node 
                    v-else 
                    v-for="node in resultTree" 
                    :key="node.path" 
                    :node="node"
                    :task-id="taskInfo.taskId || taskInfo.id"
                    @download="downloadNode"
                  />
                </div>
              </div>
            </div>
            
            <!-- 分析失败 -->
          
            
            <div v-else class="waiting-content">
              <div class="waiting-icon">⏳</div>
              <div class="waiting-text">任务执行中，请稍候...</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧配置区 -->
      <div class="right-panel">
        <div class="panel-header">参数配置</div>
        
        <div class="params-form">
          <!-- 必填参数 -->
          <div class="param-section">
            <div class="section-title">必填参数</div>
            
            <!-- 已知家族蛋白序列 -->
            <div class="param-item required">
              <label class="param-label">
                已知家族蛋白序列
                <span class="required-mark">*</span>
              </label>
              <div class="file-upload-wrapper">
                <input 
                  ref="knownFileInput"
                  type="file" 
                  accept=".fasta,.fa,.faa"
                  style="display: none"
                  @change="handleFileUpload($event, 'known')"
                >
                <div v-if="!params.known" class="file-upload-area" @click="$refs.knownFileInput.click()">
                  <span class="upload-icon">📤</span>
                  <span v-if="uploadingParam === 'known'" class="upload-text">正在上传 {{ uploadProgress }}%</span>
                  <span v-else class="upload-text">点击上传FASTA文件</span>
                  <span v-if="uploadingParam !== 'known'" class="upload-hint">支持 .fasta, .fa, .faa 格式</span>
                  <div v-if="uploadingParam === 'known'" class="upload-progress-track">
                    <div class="upload-progress-fill" :style="{ width: uploadProgress + '%' }"></div>
                  </div>
                </div>
                <div v-else class="file-selected">
                  <span class="file-icon">📄</span>
                  <span class="file-name">{{ params.knownName || '已上传文件' }}</span>
                  <button class="remove-btn" @click="removeFile('known')">✕</button>
                </div>
              </div>
              <div class="param-desc">上传已知基因家族的蛋白序列（FASTA格式）</div>
            </div>

            <!-- 基因组文件夹 -->
            <div class="param-item required">
              <label class="param-label">
                基因组文件夹
                <span class="required-mark">*</span>
              </label>
              <div class="folder-select-wrapper">
                <div v-if="!params.fa" class="folder-select-btn" @click="showFolderDialog = true">
                  <span>📁</span> 选择基因组文件夹
                </div>
                <div v-else class="folder-selected">
                  <span class="folder-icon">📂</span>
                  <span class="folder-name">{{ params.fa }}</span>
                  <button class="change-btn" @click="showFolderDialog = true">更改</button>
                </div>
              </div>
              <div class="param-desc">从服务器选择目标基因组数据文件夹</div>
            </div>

            <!-- 家族简写名称 -->
            <div class="param-item required">
              <label class="param-label">
                家族简写名称
                <span class="required-mark">*</span>
              </label>
              <input 
                v-model="params.Fname" 
                type="text" 
                class="param-input"
                placeholder="例如：WRKY, MYB, bHLH"
              >
              <div class="param-desc">家族简写英文名字，用于基因重命名（不支持中文）</div>
            </div>
          </div>

          <!-- 可选参数 -->
          <div class="param-section">
            <div class="section-title">
              可选参数
              <button class="toggle-btn" @click="showOptional = !showOptional">
                {{ showOptional ? '收起' : '展开' }}
              </button>
            </div>
            
            <div v-show="showOptional" class="optional-params">
              <!-- Pfam文件 -->
              <div class="param-item">
                <label class="param-label">Pfam文件</label>
                <div class="file-upload-wrapper">
                  <input 
                    ref="pfamFileInput"
                    type="file" 
                    accept=".hmm,.HMM"
                    style="display: none"
                    @change="handleFileUpload($event, 'pfam')"
                  >
                  <div v-if="!params.pfam" class="file-upload-area small" @click="$refs.pfamFileInput.click()">
                    <span v-if="uploadingParam === 'pfam'" class="upload-text">正在上传 {{ uploadProgress }}%</span>
                    <span v-else class="upload-text">点击上传Pfam文件</span>
                    <div v-if="uploadingParam === 'pfam'" class="upload-progress-track">
                      <div class="upload-progress-fill" :style="{ width: uploadProgress + '%' }"></div>
                    </div>
                  </div>
                  <div v-else class="file-selected small">
                    <span class="file-icon">📄</span>
                    <span class="file-name">{{ params.pfamName || '已上传' }}</span>
                    <button class="remove-btn" @click="removeFile('pfam')">✕</button>
                  </div>
                </div>
              </div>

              <!-- 比对过滤比值 -->
              <div class="param-item">
                <label class="param-label">
                  比对过滤比值 ({{ params.ratio || 0.5 }})
                </label>
                <input 
                  v-model.number="params.ratio" 
                  type="range" 
                  class="param-range"
                  min="0.1"
                  max="0.8"
                  step="0.1"
                >
                <div class="range-labels">
                  <span>宽松 (0.1)</span>
                  <span>默认 (0.5)</span>
                  <span>严格 (0.8)</span>
                </div>
              </div>

              <!-- 分组文件 -->
              <div class="param-item">
                <label class="param-label">分组文件</label>
                <div class="file-upload-wrapper">
                  <input 
                    ref="groupFileInput"
                    type="file" 
                    accept=".txt,.csv,.tsv"
                    style="display: none"
                    @change="handleFileUpload($event, 'group')"
                  >
                  <div v-if="!params.group" class="file-upload-area small" @click="$refs.groupFileInput.click()">
                    <span v-if="uploadingParam === 'group'" class="upload-text">正在上传 {{ uploadProgress }}%</span>
                    <span v-else class="upload-text">点击上传分组文件</span>
                    <div v-if="uploadingParam === 'group'" class="upload-progress-track">
                      <div class="upload-progress-fill" :style="{ width: uploadProgress + '%' }"></div>
                    </div>
                  </div>
                  <div v-else class="file-selected small">
                    <span class="file-icon">📄</span>
                    <span class="file-name">{{ params.groupName || '已上传' }}</span>
                    <button class="remove-btn" @click="removeFile('group')">✕</button>
                  </div>
                </div>
              </div>

              <!-- 最低一致性 -->
              <div class="param-item">
                <label class="param-label">最低一致性 (%)</label>
                <input 
                  v-model.number="params.identity" 
                  type="number" 
                  class="param-input"
                  min="0"
                  max="100"
                  placeholder="0"
                >
              </div>

              <!-- 最低得分 -->
              <div class="param-item">
                <label class="param-label">最低得分</label>
                <input 
                  v-model.number="params.score" 
                  type="number" 
                  class="param-input"
                  min="0"
                  placeholder="0"
                >
              </div>

              <!-- 开关参数 -->
              <div class="param-item checkbox">
                <label class="checkbox-label">
                  <input v-model="params.combine" type="checkbox">
                  <span class="check-text">取并集（BLAST+HMMER）</span>
                </label>
              </div>

              <div class="param-item checkbox">
                <label class="checkbox-label">
                  <input v-model="params.only" type="checkbox">
                  <span class="check-text">仅用HMMER结果</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- 底部执行按钮 -->
        <div class="bottom-actions">
          <button 
            class="primary-btn full" 
            :class="{ 'danger-btn': taskInfo && (taskInfo.status === 'RUNNING' || taskInfo.status === 'PENDING') }"
            @click="handleTaskAction" 
            :disabled="loading && (!taskInfo || (taskInfo.status !== 'RUNNING' && taskInfo.status !== 'PENDING'))"
          >
            <span v-if="loading && (!taskInfo || (taskInfo.status !== 'RUNNING' && taskInfo.status !== 'PENDING'))" class="loading-icon">⏳</span>
            {{ taskActionText }}
          </button>
        </div>
      </div>
    </div>

    <!-- 基因组文件夹选择弹窗 -->
    <div v-if="showFolderDialog" class="dialog-mask" @click.self="showFolderDialog = false">
      <div class="dialog folder-dialog">
        <h3>选择基因组文件夹</h3>
        <div class="folder-list">
          <div v-if="genomeFolders.length === 0" class="loading-folders">
            加载中...
          </div>
          <div 
            v-for="folder in genomeFolders" 
            :key="folder.path"
            :class="['folder-item', { selected: selectedFolder === folder.path }]"
            @click="selectedFolder = folder.path"
          >
            <span class="folder-icon">📁</span>
            <div class="folder-info">
              <div class="folder-name">{{ folder.name }}</div>
              <div v-if="folder.description" class="folder-desc">{{ folder.description }}</div>
            </div>
          </div>
        </div>
        <div class="dialog-footer">
          <button class="ghost-btn" @click="showFolderDialog = false">取消</button>
          <button class="primary-btn" @click="confirmFolder" :disabled="!selectedFolder">确定</button>
        </div>
      </div>
    </div>

    <!-- Toast 提示 -->
    <div v-if="showToast" :class="['toast', toastType]">
      {{ toastMessage }}
    </div>

    <!-- 登录弹窗 -->
    <LoginDialog :visible.sync="showLoginDialog" @success="onLoginSuccess" @switch-register="showRegisterDialog = true" />
    <!-- 注册弹窗 -->
    <RegisterDialog :visible.sync="showRegisterDialog" @switch-login="showLoginDialog = true" />
  </div>
</template>

<script>
import { 
  getToolDetail, 
  getGenomeFolders,
  uploadToolFile,
  createGeneFamilyTask,
  getTaskStatus,
  getTaskLogs,
  downloadResult,
  getTaskResultTree,
  downloadResultFile,
  cancelTask
} from '../api'
import { isLoginRequired } from '../utils/auth'

// 递归树状节点组件
const TreeNode = {
  name: 'TreeNode',
  props: {
    node: { type: Object, required: true },
    taskId: { type: String, required: true },
    level: { type: Number, default: 0 }
  },
  data() {
    return {
      expanded: true
    }
  },
  template: `
    <div class="tree-node" :style="{ paddingLeft: level * 20 + 'px' }">
      <div class="tree-node-content" @click="toggle">
        <span v-if="node.type === 'directory'" class="tree-toggle">
          {{ expanded ? '▼' : '▶' }}
        </span>
        <span v-else class="tree-toggle-placeholder"></span>
        
        <span class="tree-icon">{{ node.type === 'directory' ? '📁' : '📄' }}</span>
        <span class="tree-name">{{ node.name }}</span>
        <span v-if="node.type === 'file'" class="tree-size">{{ formatSize(node.size) }}</span>
        
        <button 
          class="tree-download-btn" 
          @click.stop="download"
          :title="node.type === 'directory' ? '下载文件夹' : '下载文件'"
        >
          ⬇
        </button>
      </div>
      
      <div v-if="node.type === 'directory' && expanded && node.children" class="tree-children">
        <tree-node 
          v-for="child in node.children" 
          :key="child.path"
          :node="child"
          :task-id="taskId"
          :level="level + 1"
          @download="$emit('download', $event)"
        />
      </div>
    </div>
  `,
  methods: {
    toggle() {
      if (this.node.type === 'directory') {
        this.expanded = !this.expanded
      }
    },
    download() {
      this.$emit('download', {
        path: this.node.path,
        type: this.node.type,
        name: this.node.name
      })
    },
    formatSize(bytes) {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }
  }
}

export default {
  name: 'GeneFamilyTool',
  components: {
    TreeNode
  },
  props: ['id'],
  data() {
    return {
      toolInfo: {},
      activeTab: 'guide', // guide, logs, result
      
      // 表单参数
      params: {
        known: '',
        knownName: '',
        fa: '',
        Fname: '',
        pfam: '',
        pfamName: '',
        ratio: 0.5,
        group: '',
        groupName: '',
        identity: 0,
        score: 0,
        combine: false,
        only: false
      },
      
      // 基因组文件夹
      showFolderDialog: false,
      genomeFolders: [],
      selectedFolder: '',
      showOptional: true,
      
      // 任务
      taskInfo: null,
      logs: [],
      resultFiles: [],
      resultTree: [],
      loadingTree: false,
      loading: false,
      
      // 轮询定时器
      pollingTimer: null,
      
      // UI状态
      showToast: false,
      toastMessage: '',
      toastType: 'success',
      // 上传进度
      uploadProgress: 0,
      uploadingParam: null,
      // 登录弹窗
      showLoginDialog: false,
      showRegisterDialog: false,
      pendingAction: null
    }
  },
  computed: {
    isFormValid() {
      return this.params.known && this.params.fa && this.params.Fname.trim()
    },
    canShowResult() {
      return this.taskInfo && (this.taskInfo.status === 'SUCCESS' || this.taskInfo.status === 'FAILED')
    },
    taskActionText() {
      if (!this.taskInfo) return '开始分析'
      if (this.taskInfo.status === 'RUNNING' || this.taskInfo.status === 'PENDING') return '结束任务'
      if (this.taskInfo.status === 'SUCCESS' || this.taskInfo.status === 'FAILED' || this.taskInfo.status === 'CANCELLED') return '查看结果'
      return '开始分析'
    },
    isTaskRunning() {
      return this.taskInfo && (this.taskInfo.status === 'RUNNING' || this.taskInfo.status === 'PENDING')
    }
  },
  async mounted() {
    await this.loadToolInfo()
    await this.loadGenomeFolders()
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    async loadToolInfo() {
      try {
        const res = await getToolDetail(this.id)
        if (res.code === 200) {
          this.toolInfo = res.data
        }
      } catch (error) {
        this.showToastMessage('加载工具信息失败', 'error')
      }
    },
    
    async loadGenomeFolders() {
      try {
        const res = await getGenomeFolders()
        if (res.code === 200) {
          this.genomeFolders = res.data
        }
      } catch (error) {
        this.showToastMessage('加载基因组文件夹失败', 'error')
      }
    },
    
    // ============ 文件上传 ============
    async handleFileUpload(event, paramName) {
      const file = event.target.files[0]
      if (!file) return
      
      this.loading = true
      this.uploadingParam = paramName
      this.uploadProgress = 0
      try {
        const formData = new FormData()
        formData.append('file', file)
        
        const res = await uploadToolFile(this.id, paramName, formData, (progressEvent) => {
          if (progressEvent.lengthComputable) {
            this.uploadProgress = Math.round((progressEvent.loaded / progressEvent.total) * 100)
          }
        })
        if (res.code === 200) {
          this.params[paramName] = res.data.savedPath
          this.params[paramName + 'Name'] = file.name
          this.showToastMessage('文件上传成功', 'success')
        }
      } catch (error) {
        this.showToastMessage('文件上传失败', 'error')
      } finally {
        this.loading = false
        this.uploadingParam = null
        this.uploadProgress = 0
      }
    },
    
    removeFile(paramName) {
      this.params[paramName] = ''
      this.params[paramName + 'Name'] = ''
    },
    
    // ============ 文件夹选择 ============
    confirmFolder() {
      if (this.selectedFolder) {
        this.params.fa = this.selectedFolder
        this.showFolderDialog = false
      }
    },
    
    // ============ 任务执行 ============
    handleTaskAction() {
      if (this.isTaskRunning) {
        this.stopTask()
      } else if (this.taskInfo && (this.taskInfo.status === 'SUCCESS' || this.taskInfo.status === 'FAILED' || this.taskInfo.status === 'CANCELLED')) {
        this.activeTab = 'result'
      } else {
        this.runTask()
      }
    },

    async runTask() {
      const token = localStorage.getItem('token')
      if (isLoginRequired() && !token) {
        this.showToastMessage('请先登录后再执行任务', 'warning')
        this.showLoginDialog = true
        this.pendingAction = 'runTask'
        return
      }

      if (!this.isFormValid) {
        this.showToastMessage('请填写所有必填参数', 'warning')
        return
      }
      
      this.loading = true
      try {
        // 构建任务参数
        const taskParams = {
          known: this.params.known,
          fa: this.params.fa,
          Fname: this.params.Fname,
          ratio: this.params.ratio,
          identity: this.params.identity,
          score: this.params.score,
          combine: this.params.combine,
          only: this.params.only
        }
        
        // 添加可选的文件参数
        if (this.params.pfam) taskParams.pfam = this.params.pfam
        if (this.params.group) taskParams.group = this.params.group
        
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
        const res = await createGeneFamilyTask({
          toolId: parseInt(this.id),
          datasetId: 'gene-family-' + Date.now(),
          params: taskParams,
          userId: userInfo.user_id || 0
        })
        
        if (res.code === 200) {
          this.taskInfo = res.data
          this.showToastMessage('任务已创建，开始执行', 'success')
          this.activeTab = 'logs'
          this.startLogPolling(res.data.taskId)
        }
      } catch (error) {
        this.showToastMessage('创建任务失败', 'error')
      } finally {
        this.loading = false
      }
    },

    async stopTask() {
      if (!this.taskInfo || !this.taskInfo.taskId) return
      
      if (!confirm('确定要终止当前任务吗？')) return
      
      try {
        const res = await cancelTask(this.taskInfo.taskId)
        if (res.code === 200) {
          this.showToastMessage('任务已终止', 'info')
          if (this.pollingTimer) {
            clearInterval(this.pollingTimer)
            this.pollingTimer = null
          }
          this.taskInfo = { ...this.taskInfo, status: 'CANCELLED', progress: 0 }
        }
      } catch (error) {
        this.showToastMessage('终止任务失败：' + (error.response?.data?.message || error.message), 'error')
      }
    },
    
    // ============ 日志轮询 ============
    startLogPolling(taskId) {
      console.log('Starting log polling for task:', taskId)
      
      // 清除已有定时器
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer)
      }
      
      // 立即获取一次日志
      this.fetchLogs(taskId)
      
      // 每2秒轮询一次
      this.pollingTimer = setInterval(() => {
        // 如果任务已完成或已终止，停止轮询
        if (this.taskInfo && (this.taskInfo.status === 'SUCCESS' || this.taskInfo.status === 'FAILED' || this.taskInfo.status === 'CANCELLED')) {
          clearInterval(this.pollingTimer)
          this.pollingTimer = null
          return
        }
        
        this.fetchLogs(taskId)
      }, 2000)
    },
    
    // 获取日志
    async fetchLogs(taskId) {
      // 检查 taskId 是否有效
      if (!taskId) {
        console.error('fetchLogs: taskId is undefined')
        return
      }
      
      try {
        // 同时获取任务状态和日志
        const [statusRes, logsRes] = await Promise.all([
          getTaskStatus(taskId),
          getTaskLogs(taskId)
        ])
        
        // 更新任务状态
        if (statusRes.code === 200 && statusRes.data) {
          const prevStatus = this.taskInfo?.status
          // 使用展开运算符确保响应式更新
          this.taskInfo = { ...statusRes.data }
          
          console.log('fetchLogs: status updated from', prevStatus, 'to', statusRes.data.status)
          
          // 如果任务完成（成功、失败或已终止），加载结果并停止轮询
          if (statusRes.data.status === 'SUCCESS' || statusRes.data.status === 'FAILED' || statusRes.data.status === 'CANCELLED') {
            console.log('Task completed with status:', statusRes.data.status)
            this.resultFiles = statusRes.data.result_files || []
            
            // 加载目录树
            if (statusRes.data.status !== 'CANCELLED') {
              await this.loadResultTree()
            }
            
            // 停止轮询
            if (this.pollingTimer) {
              clearInterval(this.pollingTimer)
              this.pollingTimer = null
              console.log('Polling stopped')
            }
          }
        } else {
          console.error('fetchLogs: invalid status response', statusRes)
        }
        
        // 更新日志
        if (logsRes.code === 200 && logsRes.data.logs) {
          const newLogs = logsRes.data.logs
          
          // 合并新日志（避免重复）
          newLogs.forEach(newLog => {
            const exists = this.logs.some(existing => 
              existing.time === newLog.time && existing.message === newLog.message
            )
            if (!exists) {
              this.logs.push(newLog)
            }
          })
          
          // 自动滚动到底部
          if (newLogs.length > 0) {
            this.$nextTick(() => {
              const container = this.$refs.logsContainer
              if (container) {
                container.scrollTop = container.scrollHeight
              }
            })
          }
        }
      } catch (error) {
        console.error('Fetch logs error:', error)
      }
    },
    
    // 停止轮询
    // 处理结果按钮点击
    handleResultClick() {
      console.log('Result button clicked, current status:', this.taskInfo?.status)
      
      if (!this.taskInfo) {
        this.showToastMessage('请先创建任务', 'warning')
        return
      }
      
      if (this.taskInfo.status === 'SUCCESS' || this.taskInfo.status === 'FAILED') {
        this.activeTab = 'result'
        // 加载结果数据
        this.loadTaskResult()
      } else {
        this.showToastMessage(`任务${this.taskInfo.status === 'RUNNING' ? '运行中' : '等待中'}，请稍后再试`, 'info')
      }
    },
    
    stopPolling() {
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer)
        this.pollingTimer = null
        console.log('Log polling stopped')
      }
    },
    
    // ============ 结果处理 ============
    async loadTaskResult() {
      if (!this.taskInfo) {
        console.error('loadTaskResult: taskInfo is null')
        return
      }
      
      try {
        const taskId = this.taskInfo.taskId || this.taskInfo.id
        console.log('loadTaskResult: loading for taskId:', taskId, 'current status:', this.taskInfo.status)
        
        if (!taskId) {
          console.error('loadTaskResult: taskId is undefined')
          return
        }
        const res = await getTaskStatus(taskId)
        console.log('loadTaskResult: status API response:', res)
        
        if (res.code === 200 && res.data) {
          this.taskInfo = { ...res.data }
          this.resultFiles = res.data.result_files || []
          console.log('loadTaskResult: taskInfo updated, status:', this.taskInfo.status)
          
          // 加载目录树
          await this.loadResultTree()
        } else {
          console.error('loadTaskResult: invalid response', res)
        }
      } catch (error) {
        console.error('加载结果失败:', error)
      }
    },
    
    async loadResultTree() {
      if (!this.taskInfo) {
        console.error('loadResultTree: taskInfo is null')
        return
      }
      
      const taskId = this.taskInfo.taskId || this.taskInfo.id
      console.log('loadResultTree: loading for taskId:', taskId)
      
      this.loadingTree = true
      try {
        const res = await getTaskResultTree(taskId)
        console.log('loadResultTree: API response:', res)
        
        if (res.code === 200 && res.data) {
          this.resultTree = res.data.tree || []
          console.log('loadResultTree: tree loaded, items:', this.resultTree.length)
        } else {
          console.error('loadResultTree: invalid response', res)
          this.resultTree = []
        }
      } catch (error) {
        console.error('加载目录树失败:', error)
        this.resultTree = []
      } finally {
        this.loadingTree = false
      }
    },
    
    // 下载树节点（文件或文件夹）
    async downloadNode({ path, type, name }) {
      try {
        this.showToastMessage(`正在下载 ${name}...`, 'info')
        
        const res = await downloadResultFile(this.taskInfo.taskId || this.taskInfo.id, path)
        const blob = new Blob([res])
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        
        // 根据类型设置下载文件名
        if (type === 'directory') {
          a.download = `${name}.zip`
        } else {
          a.download = name
        }
        
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
        
        this.showToastMessage(`${name} 下载完成`, 'success')
      } catch (error) {
        this.showToastMessage('下载失败', 'error')
        console.error('Download error:', error)
      }
    },
    
    async downloadResultFile(filename) {
      try {
        const taskId = this.taskInfo.taskId || this.taskInfo.id
        if (!taskId) return
        const res = await downloadResult(taskId, filename)
        const blob = new Blob([res])
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename.split('/').pop()
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        this.showToastMessage('下载失败', 'error')
      }
    },
    
    async downloadAllResults() {
      try {
        const taskId = this.taskInfo.taskId || this.taskInfo.id
        if (!taskId) return
        const res = await downloadResult(taskId)
        const blob = new Blob([res])
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${taskId}_results.zip`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        this.showToastMessage('下载失败', 'error')
      }
    },
    
    retryTask() {
      this.runTask()
    },
    
    // ============ 工具方法 ============
    goBack() {
      this.$emit('back')
    },
    
    formatTime(timestamp) {
      if (!timestamp) return ''
      return timestamp.split(' ')[1] || timestamp
    },
    
    formatFileSize(bytes) {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },
    
    showToastMessage(message, type = 'success') {
      this.toastMessage = message
      this.toastType = type
      this.showToast = true
      setTimeout(() => {
        this.showToast = false
      }, 3000)
    },
    onLoginSuccess() {
      this.showLoginDialog = false
      // 通知父组件登录成功，更新右上角状态
      this.$emit('login-success')
      if (this.pendingAction === 'runTask') {
        this.pendingAction = null
        this.runTask()
      }
    }
  },
  watch: {
    logs() {
      // 日志更新时滚动到底部
      this.$nextTick(() => {
        const container = this.$refs.logsContainer
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      })
    },
    // 监控 taskInfo 变化，特别是状态变化
    'taskInfo.status'(newVal, oldVal) {
      console.log(`Task status changed: ${oldVal} -> ${newVal}`)
      if (newVal === 'SUCCESS' || newVal === 'FAILED') {
        console.log('Task completed, enabling result tab')
        // 强制刷新计算属性
        this.$forceUpdate()
        // 自动切换到结果页（可选）
        // this.activeTab = 'result'
      }
    },
    taskInfo: {
      deep: true,
      handler(newVal) {
        if (newVal) {
          console.log('Task info updated:', newVal.status, newVal.taskId || newVal.id)
        }
      }
    }
  }
}
</script>

<style scoped>
/* 复用 ToolDetail.vue 的基础样式 */
.analysis-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f5f7fa;
}

.top-bar {
  height: 60px;
  background: #fff;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  flex-shrink: 0;
}

.title {
  font-size: 18px;
  font-weight: bold;
  flex: 1;
  text-align: center;
}

.main-layout {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.left-panel {
  flex: 2;
  background: #fff;
  border-right: 1px solid #eee;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chart-toolbar {
  display: flex;
  gap: 12px;
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
  flex-wrap: wrap;
}

.content-area {
  flex: 1;
  overflow: auto;
  padding: 20px;
}

.right-panel {
  flex: 1;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  min-width: 380px;
  max-width: 450px;
}

.panel-header {
  padding: 15px 20px;
  background: #fff;
  border-bottom: 1px solid #eee;
  font-weight: bold;
  font-size: 16px;
}

.params-form {
  flex: 1;
  overflow: auto;
  padding: 15px;
}

.param-section {
  margin-bottom: 20px;
  padding: 15px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #ebeef5;
}

.section-title {
  font-weight: bold;
  font-size: 14px;
  color: #303133;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.toggle-btn {
  font-size: 12px;
  color: #409eff;
  background: none;
  border: none;
  cursor: pointer;
}

.param-item {
  margin-bottom: 15px;
}

.param-item.required .param-label {
  font-weight: bold;
}

.param-label {
  display: block;
  font-size: 13px;
  color: #606266;
  margin-bottom: 6px;
}

.required-mark {
  color: #f56c6c;
  margin-left: 4px;
}

.param-desc {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

.param-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 13px;
  transition: border-color 0.2s;
}

.param-input:focus {
  outline: none;
  border-color: #409eff;
}

/* 文件上传 */
.file-upload-wrapper {
  position: relative;
}

.file-upload-area {
  border: 2px dashed #dcdfe6;
  border-radius: 6px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
}

.file-upload-area:hover {
  border-color: #409eff;
  background: #f5f7fa;
}

.file-upload-area.small {
  padding: 12px;
}

.upload-icon {
  font-size: 24px;
  display: block;
  margin-bottom: 8px;
}

.upload-text {
  font-size: 13px;
  color: #606266;
}

.upload-hint {
  font-size: 11px;
  color: #909399;
  margin-top: 4px;
}

.upload-progress-track {
  width: 100%;
  height: 6px;
  background: #e2e8f0;
  border-radius: 3px;
  margin-top: 10px;
  overflow: hidden;
}

.upload-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #0ea5e9, #0284c7);
  border-radius: 3px;
  transition: width 0.2s ease;
}

.file-selected {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  background: #f0f9eb;
  border: 1px solid #67c23a;
  border-radius: 4px;
}

.file-selected.small {
  padding: 8px;
}

.file-icon {
  font-size: 20px;
}

.file-name {
  flex: 1;
  font-size: 13px;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.remove-btn {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: none;
  background: #f56c6c;
  color: #fff;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 文件夹选择 */
.folder-select-wrapper {
  position: relative;
}

.folder-select-btn {
  padding: 12px;
  border: 2px dashed #dcdfe6;
  border-radius: 6px;
  text-align: center;
  cursor: pointer;
  color: #606266;
  transition: all 0.2s;
}

.folder-select-btn:hover {
  border-color: #409eff;
  background: #f5f7fa;
}

.folder-selected {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  background: #ecf5ff;
  border: 1px solid #409eff;
  border-radius: 4px;
}

.folder-icon {
  font-size: 20px;
}

.folder-name {
  flex: 1;
  font-size: 13px;
  color: #303133;
}

.change-btn {
  font-size: 12px;
  color: #409eff;
  background: none;
  border: none;
  cursor: pointer;
}

/* 范围滑块 */
.param-range {
  width: 100%;
  margin: 10px 0;
}

.range-labels {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #909399;
}

/* 复选框 */
.param-item.checkbox {
  display: flex;
  align-items: center;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.check-text {
  font-size: 13px;
  color: #606266;
}

/* 底部按钮 */
.bottom-actions {
  padding: 15px;
  border-top: 1px solid #eee;
  background: #fff;
}

/* 使用指南 */
.guide-panel h3 {
  color: #303133;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid #409eff;
}

.guide-content {
  line-height: 1.8;
  color: #606266;
}

.guide-content h4 {
  color: #303133;
  margin: 20px 0 10px;
}

.guide-content ul {
  padding-left: 20px;
  margin: 10px 0;
}

.guide-content li {
  margin-bottom: 8px;
}

/* 日志面板 */
.logs-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background: #f5f7fa;
  border-bottom: 1px solid #eee;
}

.logs-title {
  font-weight: bold;
  color: #303133;
}

.polling-status {
  font-size: 12px;
  font-weight: bold;
  color: #409eff;
}

.logs-container {
  height: calc(100% - 40px);
  overflow: auto;
  padding: 10px;
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 12px;
  background: #1e1e1e;
  color: #d4d4d4;
}

.log-item {
  padding: 4px 0;
  border-bottom: 1px solid #333;
  display: flex;
  gap: 10px;
}

.log-time {
  color: #858585;
  white-space: nowrap;
  min-width: 70px;
}

.log-type-badge {
  min-width: 50px;
  text-align: center;
  padding: 0 4px;
  border-radius: 3px;
  font-size: 10px;
  text-transform: uppercase;
}

.log-item.info .log-type-badge {
  background: #3c3c3c;
  color: #75beff;
}

.log-item.success .log-type-badge {
  background: #1e4620;
  color: #89d185;
}

.log-item.warning .log-type-badge {
  background: #4d4d1f;
  color: #cca700;
}

.log-item.error .log-type-badge {
  background: #4d1f1f;
  color: #f48771;
}

.empty-logs {
  text-align: center;
  color: #858585;
  padding: 40px;
}

/* 结果面板 */
.result-content {
  padding: 20px;
}

.result-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 30px;
  padding: 20px;
  background: #f0f9eb;
  border-radius: 8px;
}

.success-icon {
  font-size: 32px;
}

.success-text {
  font-size: 18px;
  color: #67c23a;
  font-weight: bold;
}

/* 输出目录信息 */
.output-dir-info {
  margin: 20px 0;
  padding: 15px;
  background: #f5f7fa;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
}

.dir-label {
  font-size: 13px;
  color: #606266;
  margin-bottom: 8px;
  font-weight: bold;
}

.dir-path {
  font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
  font-size: 12px;
  color: #409eff;
  background: #fff;
  padding: 10px;
  border-radius: 4px;
  border: 1px solid #dcdfe6;
  word-break: break-all;
  line-height: 1.5;
}

/* 结果树状面板 */
.result-tree-panel {
  margin-top: 20px;
}

.tree-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
}

.tree-header h4 {
  margin: 0;
  color: #303133;
}

.tree-container {
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #fff;
  max-height: 500px;
  overflow: auto;
  padding: 10px;
}

.loading-tree,
.empty-tree {
  text-align: center;
  padding: 40px;
  color: #909399;
}

/* 树状节点样式 */
.tree-node {
  margin: 2px 0;
}

.tree-node-content {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;
}

.tree-node-content:hover {
  background: #f5f7fa;
}

.tree-toggle {
  width: 16px;
  text-align: center;
  font-size: 10px;
  color: #909399;
  cursor: pointer;
}

.tree-toggle-placeholder {
  width: 16px;
}

.tree-icon {
  font-size: 16px;
}

.tree-name {
  flex: 1;
  font-size: 13px;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tree-size {
  font-size: 11px;
  color: #909399;
  min-width: 60px;
  text-align: right;
}

.tree-download-btn {
  padding: 4px 8px;
  background: #409eff;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s;
}

.tree-node-content:hover .tree-download-btn {
  opacity: 1;
}

.tree-download-btn:hover {
  background: #66b1ff;
}

.tree-children {
  border-left: 1px dashed #dcdfe6;
  margin-left: 8px;
}

/* 旧样式兼容 */
.result-files {
  margin-bottom: 30px;
}

.result-files h4 {
  margin-bottom: 15px;
  color: #303133;
}

.file-list {
  border: 1px solid #ebeef5;
  border-radius: 6px;
  overflow: hidden;
}

.file-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  border-bottom: 1px solid #ebeef5;
  background: #fff;
}

.file-item:last-child {
  border-bottom: none;
}

.file-name {
  flex: 1;
  font-size: 13px;
  color: #606266;
}

.file-size {
  font-size: 12px;
  color: #909399;
}

.download-btn {
  padding: 4px 12px;
  background: #409eff;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
}

.result-actions {
  text-align: center;
}

.error-content {
  text-align: center;
  padding: 40px;
}

.error-icon {
  font-size: 48px;
  margin-bottom: 10px;
}

.error-text {
  font-size: 18px;
  color: #f56c6c;
  margin-bottom: 10px;
}

.error-message {
  color: #909399;
  margin-bottom: 20px;
}

.waiting-content {
  text-align: center;
  padding: 60px 20px;
}

.waiting-icon {
  font-size: 48px;
  margin-bottom: 10px;
  animation: pulse 1.5s infinite;
}

.waiting-text {
  color: #909399;
}

/* 弹窗 */
.dialog-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}

.dialog {
  background: #fff;
  border-radius: 8px;
  width: 500px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.dialog h3 {
  padding: 15px 20px;
  margin: 0;
  border-bottom: 1px solid #eee;
}

.folder-list {
  flex: 1;
  overflow: auto;
  padding: 10px;
  max-height: 400px;
}

.loading-folders {
  text-align: center;
  padding: 40px;
  color: #909399;
}

.folder-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s;
}

.folder-item:hover {
  background: #f5f7fa;
}

.folder-item.selected {
  background: #ecf5ff;
  border: 1px solid #409eff;
}

.folder-info {
  flex: 1;
}

.folder-name {
  font-weight: bold;
  color: #303133;
}

.folder-desc {
  font-size: 12px;
  color: #909399;
  margin-top: 2px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 15px 20px;
  border-top: 1px solid #eee;
}

/* 通用按钮样式 */
.tool-btn {
  background: #f5f5f5;
  border: 1px solid #ddd;
  padding: 8px 14px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
}

.tool-btn:hover:not(:disabled) {
  background: #e6f4ff;
  border-color: #409eff;
}

.tool-btn.primary {
  background: #e6f4ff;
  border-color: #409eff;
  color: #409eff;
}

.tool-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.tool-btn.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-status {
  font-size: 11px;
  color: #909399;
  margin-left: 4px;
}

.primary-btn {
  background: #409eff;
  color: #fff;
  border: none;
  padding: 8px 18px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.primary-btn:hover:not(:disabled) {
  background: #66b1ff;
}

.primary-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.primary-btn.full {
  width: 100%;
  justify-content: center;
  padding: 12px;
  font-size: 14px;
}

.primary-btn.danger-btn {
  background: #ef4444;
}

.primary-btn.danger-btn:hover:not(:disabled) {
  background: #dc2626;
}

.ghost-btn {
  background: #fff;
  border: 1px solid #dcdfe6;
  padding: 8px 18px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
}

.ghost-btn:hover {
  border-color: #409eff;
  color: #409eff;
}

.loading-icon {
  animation: rotate 1s linear infinite;
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* Toast */
.toast {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  padding: 12px 24px;
  border-radius: 4px;
  color: #fff;
  font-size: 14px;
  z-index: 9999;
  animation: fadeIn 0.3s;
}

.toast.success { background: #67c23a; }
.toast.error { background: #f56c6c; }
.toast.warning { background: #e6a23c; }

@keyframes fadeIn {
  from { opacity: 0; transform: translateX(-50%) translateY(-20px); }
  to { opacity: 1; transform: translateX(-50%) translateY(0); }
}
</style>
